import type { MonitoredServer } from "@xop/dashboard-shared";

/**
 * Single source of truth for a monitored server's *overall* health on
 * the Server Status page — the accent stripe, the card tone, and the
 * All / Healthy / Broken filter all read from here.
 */

export type ServerTone = "good" | "warn" | "bad" | "muted";

/**
 * Collapse a server's probe results into one overall tone:
 *   - `bad`   — unreachable (the dominant failure; trumps everything)
 *   - `warn`  — reachable, but the TLS certificate is invalid
 *   - `good`  — reachable and nothing wrong → fully healthy
 *   - `muted` — first probe still pending (`reachable === null`)
 */
export function rowTone(server: MonitoredServer): ServerTone {
  if (server.reachable === false) return "bad";
  if (server.ssl && !server.ssl.valid) return "warn";
  if (server.reachable === true) return "good";
  return "muted";
}

/** The three states of the Server Status health filter. */
export type ServerHealthFilter = "all" | "healthy" | "broken";

/**
 * A server is "fully healthy" only when its overall tone is `good`.
 * Everything else — down, bad certificate, or still checking — counts
 * as "broken" so the two filtered views always partition the full list
 * (`healthy ∪ broken === all`); no server is ever hidden from both.
 */
export function isServerHealthy(server: MonitoredServer): boolean {
  return rowTone(server) === "good";
}

/** Does this server belong in the given filtered view? */
export function matchesHealthFilter(
  server: MonitoredServer,
  filter: ServerHealthFilter,
): boolean {
  if (filter === "all") return true;
  return filter === "healthy" ? isServerHealthy(server) : !isServerHealthy(server);
}

export interface HealthCounts {
  all: number;
  healthy: number;
  broken: number;
}

/** Counts for the filter-pill labels. `all === healthy + broken`. */
export function countByHealth(servers: readonly MonitoredServer[]): HealthCounts {
  let healthy = 0;
  for (const s of servers) {
    if (isServerHealthy(s)) healthy += 1;
  }
  return { all: servers.length, healthy, broken: servers.length - healthy };
}
